import {
  AQL_LEVEL,
  INSPECTON_LEVEL,
  SAMPLE_SIZES,
  AQL_TYPE,
  AQL_TYPE_VALUE,
  aql_sample_size_code,
} from '@/constant/aql'
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Radio,
  Select,
  Switch,
  message,
  notification,
} from 'antd'
import './AddInspectionAQLModal.style.less'
import { useEffect, useState } from 'react'
import { isEmptyValue } from '@/utils/common'
import { useHasMeasurementAql } from '@/hook/useMeasurementAql'

import {
  saveInspectionAQL,
  updateInspectionAQL,
} from '../../../DataManagement.transform'
import { useMutation } from 'react-query'
import { queryClient } from '@/config/react-query'
import { orderBy, uniqBy } from 'lodash'

interface Props {
  visible: boolean
  onClose: () => void
  data: any
  propertyId: string
}

function AddInspectionAQLModal(props: Props) {
  const [form] = Form.useForm()

  const [isDefault, setisDefault] = useState(false)
  const [sampleSizes, setsampleSizes] = useState([])
  const [aqlType, setAqlType] = useState(props.data?.aql_type ?? AQL_TYPE_VALUE.DEFAULT)

  const hasMeasurementAqlSetting = useHasMeasurementAql()

  const [isMinSS, setisMinSS] = useState(
    !isEmptyValue(props.data?.min_sample_size) ? true : false
  )
  const [isMaxSS, setisMaxSS] = useState(
    !isEmptyValue(props.data?.max_sample_size) ? true : false
  )

  const mutation = useMutation(async (data: any) => {
    return await saveInspectionAQL(props.propertyId, data, isDefault)
  })

  const updateMutation = useMutation(async (data: any) => {
    return await updateInspectionAQL(props.data.id, data, isDefault)
  })

  const onSave = async (values) => {
    const payload = {
      name: values.name,
      inspection_level: values.inspection_level,
      aql_type: aqlType,
      critical_level: aqlType === AQL_TYPE_VALUE.DEFAULT ? values.critical_level : undefined,
      major_level: aqlType === AQL_TYPE_VALUE.DEFAULT ? values.major_level : undefined,
      minor_level: aqlType === AQL_TYPE_VALUE.DEFAULT ? values.minor_level : undefined,
      other_level: aqlType === AQL_TYPE_VALUE.DEFAULT ? values?.other_level : undefined,
      min_sample_size: values?.min_sample_size?.toString(),
      max_sample_size: values?.max_sample_size?.toString(),
    }
    if (!isEmptyValue(props.data)) {
      const res = await updateMutation.mutateAsync(payload)
      if (isEmptyValue(res?.data)) {
        notification.error({ message: res, duration: 3, placement: 'top' })
        return
      }
    } else {
      const res = await mutation.mutateAsync(payload)
      if (isEmptyValue(res?.data)) {
        notification.error({ message: res, duration: 3, placement: 'top' })
        return
      }
    }

    await queryClient.refetchQueries(['inspectionAQL'])
    props.onClose()
  }

  useEffect(() => {
    if (updateMutation.isError) {
      console.log('updateMutation.error', updateMutation.error)

      message.error({ content: '' })
    }
  }, [updateMutation.isError])

  useEffect(() => {
    if (!isEmptyValue(props.data)) {
      form.setFieldsValue({
        name: props.data.name,
        inspection_level: props.data?.inspection_level,
        critical_level: props.data?.critical_level,
        major_level: props.data?.major_level,
        minor_level: props.data?.minor_level,
        other_level: props.data?.other_level,
        min_sample_size: !isEmptyValue(props.data?.min_sample_size)
          ? String(props.data?.min_sample_size)
          : null,
        max_sample_size: props.data?.max_sample_size
          ? String(props.data?.max_sample_size)
          : null,
      })
      setisDefault(props.data?.is_default)
      setAqlType(props.data?.aql_type ?? AQL_TYPE_VALUE.DEFAULT)
    }
  }, [props.data])

  const level = Form.useWatch('inspection_level', form)

  useEffect(() => {
    setsampleSizes(
      orderBy(
        uniqBy(
          aql_sample_size_code
            .filter((item) => item.level == level)
            .map((item) => {
              return { label: item.sampleSize, value: Number(item?.sampleSize) }
            }),
          'value'
        ),
        'value',
        'asc'
      )
    )
  }, [level])

  return (
    <Modal
      className="modal-add-supplier-container"
      open={props.visible}
      width={480}
      footer={false}
      maskClosable={false}
      onCancel={props.onClose}
    >
      <div>
        <p className="title">
          {!isEmptyValue(props.data) ? 'Edit' : 'New'} Inspection AQL
        </p>
        <Form labelCol={{ span: 7 }} onFinish={onSave} form={form}>

          {hasMeasurementAqlSetting && (
            <Row style={{ marginTop: 24, marginBottom: 24 }}>
              <Col>
                <Radio.Group
                  value={aqlType}
                  onChange={(e) => setAqlType(e.target.value)}
                >
                  {AQL_TYPE.map((opt) => (
                    <Radio key={opt.value} value={opt.value}>
                      {opt.label}
                    </Radio>
                  ))}
                </Radio.Group>
              </Col>
            </Row>
          )}

          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item
            label="Level"
            name="inspection_level"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Select
              size="large"
              placeholder="Select"
              showSearch
              options={INSPECTON_LEVEL}
            />
          </Form.Item>

          <Row>
            <Col span={7}>
              <p className="is-default-text">Default</p>
            </Col>

            <Col>
              <Checkbox
                name="Is Default"
                checked={isDefault}
                onChange={(e) => {
                  if (!props.data?.is_default) {
                    setisDefault(e.target.checked)
                  }
                }}
              />
            </Col>
          </Row>

          {aqlType === AQL_TYPE_VALUE.DEFAULT && (
            <>
              <p className="sub-title">Defect Classification</p>

              <Form.Item
                label="Critical"
                name="critical_level"
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Select
                  size="large"
                  placeholder="Select"
                  showSearch
                  options={AQL_LEVEL}
                />
              </Form.Item>

              <Form.Item
                label="Major"
                name="major_level"
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Select
                  size="large"
                  placeholder="Select"
                  showSearch
                  options={AQL_LEVEL}
                />
              </Form.Item>

              <Form.Item
                label="Minor"
                name="minor_level"
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Select
                  size="large"
                  placeholder="Select"
                  showSearch
                  options={AQL_LEVEL}
                />
              </Form.Item>

              <Form.Item
                label="Other"
                name="other_level"
                rules={[{ required: false, message: 'This field is required' }]}
              >
                <Select
                  size="large"
                  placeholder="Select"
                  showSearch
                  options={AQL_LEVEL}
                />
              </Form.Item>
            </>
          )}

          <p className="sub-title">Advanced Settings</p>
          <Row className="setting-container" justify={'space-between'}>
            <Row>
              <Switch checked={isMinSS} onChange={setisMinSS} />
              <p className="label">Min. Sample Size</p>
            </Row>
            {isMinSS && (
              <Col span={12}>
                <Form.Item
                  label=""
                  name="min_sample_size"
                  rules={[
                    { required: true, message: 'This field is required' },
                  ]}
                >
                  <Select
                    size="large"
                    placeholder="Select"
                    showSearch
                    options={sampleSizes}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          <div style={{ height: 22 }} />
          <Row className="setting-container" justify={'space-between'}>
            <Row>
              <Switch checked={isMaxSS} onChange={setisMaxSS} />
              <p className="label">Max. Sample Size</p>
            </Row>
            {isMaxSS && (
              <Col span={12}>
                <Form.Item
                  label=""
                  name="max_sample_size"
                  rules={[
                    { required: true, message: 'This field is required' },
                  ]}
                >
                  <Select
                    size="large"
                    placeholder="Select"
                    showSearch
                    options={sampleSizes}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          <div style={{ height: 22 }} />
          <Row className="button-container">
            <Button onClick={props.onClose} size="large">
              Cancel
            </Button>
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              loading={mutation.isLoading || updateMutation.isLoading}
            >
              {!isEmptyValue(props.data) ? 'Save' : 'Create'}
            </Button>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default AddInspectionAQLModal
